/**
 * Huddles (voice/video calls in a channel or DM). One active huddle per conversation, owned by a HuddleDO; the
 * WorkspaceDO mirrors it into the events log (`huddle.started` / `huddle.updated` / `huddle.ended`, audience = the
 * conversation's members) and into bootstrap.
 *
 * Media goes through Cloudflare Realtime SFU. Clients never see the SFU app secret: they talk to the SFU's HTTPS API
 * through `JoinHuddleResponse.media.path` (same paths as the Realtime SFU API: `/sessions/new`,
 * `/sessions/:sessionId/tracks/new`, `/sessions/:sessionId/renegotiate`, `/sessions/:sessionId/tracks/close`,
 * `/sessions/:sessionId/tracks/update`, plus `/generate-ice-servers`), authenticated with the usual bearer token.
 * The API only lets a participant create sessions, use their own sessions, and pull tracks published in the same
 * huddle. Each participant announces what it publishes in `HuddleParticipant.tracks`.
 */
import { z } from "zod";
import { CaptionSource, CaptionWord, FileRef, HuddleNotes, HuddleTranscriptLine, Id, Message, MeetingPolicy, Timestamp } from "./entities.ts";

/** Where one published track lives on the SFU (pull it with this pair). */
export const HuddleTrackRef = z.object({
  sessionId: z.string().min(1).max(128),
  trackName: z.string().min(1).max(128),
});
export type HuddleTrackRef = z.infer<typeof HuddleTrackRef>;

/** Tracks a participant publishes. A muted mic keeps its track (silence is sent), so unmuting needs no renegotiation. */
export const HuddleTracks = z.object({
  mic: HuddleTrackRef.optional(),
  camera: HuddleTrackRef.optional(),
  screen: HuddleTrackRef.optional(),
  screenAudio: HuddleTrackRef.optional(),
});
export type HuddleTracks = z.infer<typeof HuddleTracks>;

/**
 * Huddle themes (Slack's huddle themes): a backdrop for the whole call that everyone in it sees. Anyone in the call
 * can change it; "none" (only in requests) goes back to the plain call. Colours live in core logic/huddle `HUDDLE_THEMES`.
 */
export const HuddleTheme = z.enum(["none", "sunrise", "ocean", "forest", "galaxy", "party"]);
export type HuddleTheme = z.infer<typeof HuddleTheme>;

/**
 * How live captions are transcribed: "server" = clients send short audio clips of their own microphone
 * (`POST …/captions/audio`, Workers AI Whisper); "client" = the server has no speech model, so clients transcribe on
 * the device (Web Speech, Apple Speech, Android SpeechRecognizer) and send the text (`POST …/captions`).
 */
export const HuddleTranscription = z.enum(["server", "client"]);
export type HuddleTranscription = z.infer<typeof HuddleTranscription>;

/**
 * Why a device transcribes on the server instead of on the device (docs/specs/on-device-transcription.md): the
 * "Make captions on this device" setting is off; no engine runs here (OS, browser features); the engine doesn't speak
 * the caption language; its model isn't downloaded (or is downloading); too little storage or memory; it failed.
 */
export const TranscriptionReason = z.enum(["setting_off", "unsupported", "language", "not_downloaded", "downloading", "low_storage", "low_memory", "failed"]);
export type TranscriptionReason = z.infer<typeof TranscriptionReason>;

/** Which mode a device uses for my captions, and why when it isn't on the device (core logic/transcription). */
export const TranscriptionState = z.object({
  mode: HuddleTranscription,
  reason: TranscriptionReason.optional(),
  /** The engine, for reports ("apple-speech", "voz", "android-speech", "web-speech", "whisper"). */
  engine: z.string().max(40).optional(),
});
export type TranscriptionState = z.infer<typeof TranscriptionState>;

export const HuddleParticipant = z.object({
  /** A member's user id, or a meeting guest's id (`g_…`, see `kind`). */
  userId: Id,
  /** "guest": joined from a meeting link without a Bundle account (huddle-only; shown with an External badge). */
  kind: z.enum(["member", "guest"]).optional(),
  /** Display name at join (guests: what they typed). Clients prefer the directory's name for members. */
  name: z.string().max(80).optional(),
  joinedAt: Timestamp,
  muted: z.boolean(),
  video: z.boolean(),
  screen: z.boolean(),
  /** Set while the hand is raised (raise order = this time). */
  handRaisedAt: Timestamp.optional(),
  /**
   * My latest emoji reaction in the call (Slack huddle reactions) and when the server got it. Clients float it over
   * the tile for a few seconds from when they first see a new `at` (core logic/huddle `trackReactions`).
   */
  reaction: z.object({ emoji: z.string().min(1).max(32), at: Timestamp }).optional(),
  /**
   * My latest emoji effect (Slack huddle effects: the emoji bursts across the whole call for everyone, unlike a
   * reaction over my tile) and when the server got it. `at` also seeds the burst's layout (core logic/huddle
   * `effectBurst`), so everyone sees the same one.
   */
  effect: z.object({ emoji: z.string().min(1).max(32), at: Timestamp }).optional(),
  /**
   * I turned live captions on (Slack's per-person "Turn on captions"). While anyone in the call has them on, every
   * client transcribes its own microphone (core logic/huddle `transcribing`) and the lines are kept for the
   * transcript posted after the huddle.
   */
  captions: z.boolean().optional(),
  /** How my device transcribes my speech, as it last reported (`UpdateHuddleRequest.transcription`). */
  transcription: TranscriptionState.optional(),
  tracks: HuddleTracks,
});
export type HuddleParticipant = z.infer<typeof HuddleParticipant>;

/** Meeting settings carried in the huddle snapshot. */
export const HuddleMeeting = z.object({
  code: z.string(),
  title: z.string(),
  hostId: Id,
  policy: MeetingPolicy,
  locked: z.boolean(),
});
export type HuddleMeeting = z.infer<typeof HuddleMeeting>;

export const LobbyEntry = z.object({ guestId: Id, name: z.string(), requestedAt: Timestamp });
export type LobbyEntry = z.infer<typeof LobbyEntry>;

export const Huddle = z.object({
  id: Id,
  /** The channel or DM the huddle belongs to. */
  channelId: Id,
  startedBy: Id,
  startedAt: Timestamp,
  /** In join order. */
  participants: z.array(HuddleParticipant),
  /** Set when the huddle has a meeting link (external huddle). */
  meeting: HuddleMeeting.optional(),
  /** People waiting to be let in from the link (only in snapshots for members, never for guests). */
  lobby: z.array(LobbyEntry).optional(),
  /**
   * Someone is recording the call (the mixed call, recorded by their client and posted to the huddle's thread when it
   * stops). Everyone sees the indicator and a notice when it starts (consent).
   */
  recording: z.object({ userId: Id, startedAt: Timestamp }).optional(),
  /** The call's theme (absent = none); see HuddleTheme. */
  theme: HuddleTheme.optional(),
  /**
   * The workspace transcribes its huddles (Settings › Huddles, "Transcribe huddles"; set when the huddle starts):
   * everyone's unmuted speech is transcribed and saved for everyone in it, whether or not anyone has captions on.
   */
  transcript: z.boolean().optional(),
  /** Bumped on every change; a snapshot with a lower version than one already applied is stale. */
  version: z.number().int().nonnegative(),
});
export type Huddle = z.infer<typeof Huddle>;

/** How this deployment carries media. `unavailable`: no SFU configured (local development) — state only. */
export const HuddleMedia = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("sfu"), path: z.string() }),
  z.object({ kind: z.literal("unavailable") }),
]);
export type HuddleMedia = z.infer<typeof HuddleMedia>;

/** Joins the conversation's huddle, starting one when none is active. */
export const JoinHuddleRequest = z.object({
  muted: z.boolean().default(false),
  video: z.boolean().default(false),
});
export type JoinHuddleRequest = z.input<typeof JoinHuddleRequest>;
/**
 * `transcription` is the mode for clients from before on-device transcription ("server" whenever the server has a
 * speech model). `transcriptionModes` lists the modes this server takes, in preference order: on the device first
 * ("client"), then "server" when it has a speech model. Clients that know it pick their own mode per device
 * (core logic/transcription `selectTranscription`).
 */
export const JoinHuddleResponse = z.object({
  huddle: Huddle,
  media: HuddleMedia,
  transcription: HuddleTranscription.optional(),
  transcriptionModes: z.array(HuddleTranscription).optional(),
});
export type JoinHuddleResponse = z.infer<typeof JoinHuddleResponse>;

/** My own state in the huddle (only the fields sent change; `tracks` replaces the whole set). */
export const UpdateHuddleRequest = z.object({
  muted: z.boolean().optional(),
  video: z.boolean().optional(),
  screen: z.boolean().optional(),
  hand: z.boolean().optional(),
  tracks: HuddleTracks.optional(),
  /** Send an emoji reaction to everyone in the call (shown over my tile for a few seconds). */
  reaction: z.string().min(1).max(32).regex(/^\S+$/).optional(),
  /** Send an emoji effect that bursts across the whole call for everyone. */
  effect: z.string().min(1).max(32).regex(/^\S+$/).optional(),
  /** Change the call's theme for everyone ("none" clears it). */
  theme: HuddleTheme.optional(),
  /** Live captions on/off for me. */
  captions: z.boolean().optional(),
  /** How my device transcribes my speech now (sent at join when captions start, and whenever it changes). */
  transcription: TranscriptionState.optional(),
  /** Start (true) or stop (false) recording the call (members only; one recording at a time). */
  recording: z.boolean().optional(),
});
export type UpdateHuddleRequest = z.infer<typeof UpdateHuddleRequest>;
export const HuddleResponse = z.object({ huddle: Huddle });
export type HuddleResponse = z.infer<typeof HuddleResponse>;

// ---------- External huddles (meeting links) ----------
// A meeting is a huddle with a link: https://<web>/m/<code>. Anyone with the link reaches a public page (title and
// host name only); members of the workspace join as themselves, everyone else as an anonymous huddle-only guest after
// a Turnstile check. By default guests wait in a lobby until a member in the call admits them ("ask"); "open" lets
// them straight in; a locked meeting takes nobody new. Guests get a short-lived meeting token scoped to that one huddle
// (state, chat, their own media sessions); they never see workspace data.

/** "abc-defg-hij": 10 random letters (≈47 bits), lowercase. */
export const MeetingCode = z.string().regex(/^[a-z]{3}-[a-z]{4}-[a-z]{3}$/);

export const Meeting = z.object({
  code: MeetingCode,
  /** Shareable link: <web origin>/m/<code>. */
  url: z.string(),
  title: z.string(),
  huddleId: Id,
  /** The channel or DM the huddle (and its thread) lives in; absent for a standalone meeting. */
  channelId: Id.optional(),
  hostId: Id,
  policy: MeetingPolicy,
  locked: z.boolean(),
  scheduledAt: Timestamp.optional(),
  /** The link stops working after this (absent = until revoked). */
  expiresAt: Timestamp.optional(),
  revoked: z.boolean(),
  createdAt: Timestamp,
  /** Members invited to a standalone link (absent = none). */
  invitees: z.array(Id).optional(),
});
export type Meeting = z.infer<typeof Meeting>;

export const CreateMeetingRequest = z.object({
  title: z.string().trim().min(1).max(120),
  channelId: Id.optional(),
  scheduledAt: Timestamp.optional(),
  expiresAt: Timestamp.optional(),
  /** Absent: the workspace's default for new links (Settings → Huddles). */
  policy: MeetingPolicy.optional(),
  /** Members invited (a standalone link): they see it in their Huddles area (Upcoming, Live now). */
  invitees: z.array(Id).max(50).optional(),
});
export type CreateMeetingRequest = z.input<typeof CreateMeetingRequest>;
export const MeetingResponse = z.object({ meeting: Meeting });
export type MeetingResponse = z.infer<typeof MeetingResponse>;
/** Links I host or was invited to (revoked ones included for a while, so the UI can show them as such). */
export const ListMeetingsResponse = z.object({ meetings: z.array(Meeting) });
export type ListMeetingsResponse = z.infer<typeof ListMeetingsResponse>;
/** Settings → Huddles "Revoke all links" (admins): how many links stopped working. */
export const RevokeMeetingsResponse = z.object({ revoked: z.number().int().nonnegative() });
export type RevokeMeetingsResponse = z.infer<typeof RevokeMeetingsResponse>;

/** Host (or a workspace admin): rename, policy, lock, revoke (revoking also removes guests from the call). */
export const UpdateMeetingRequest = z.object({
  title: z.string().trim().min(1).max(120).optional(),
  policy: MeetingPolicy.optional(),
  locked: z.boolean().optional(),
  revoked: z.boolean().optional(),
});
export type UpdateMeetingRequest = z.infer<typeof UpdateMeetingRequest>;

/** A member in the call lets people in (or turns them away). */
export const LobbyDecisionRequest = z.object({
  guestIds: z.array(Id).max(100).optional(),
  all: z.boolean().optional(),
  admit: z.boolean(),
});
export type LobbyDecisionRequest = z.infer<typeof LobbyDecisionRequest>;

/** Mute or remove someone (members can act on guests; the host on anyone). "end" ends the huddle for everyone (host). */
export const ModerateHuddleRequest = z.discriminatedUnion("action", [
  z.object({ action: z.literal("mute"), userId: Id }),
  z.object({ action: z.literal("remove"), userId: Id }),
  z.object({ action: z.literal("end") }),
]);
export type ModerateHuddleRequest = z.infer<typeof ModerateHuddleRequest>;

/** Public, unauthenticated view of a link: the title and the host's name, nothing else from the workspace. */
export const PublicMeeting = z.object({
  code: MeetingCode,
  title: z.string(),
  hostName: z.string(),
  /** "scheduled": not started yet (scheduledAt in the future, nobody in); "live": people are in; "open": joinable. */
  status: z.enum(["scheduled", "open", "live"]),
  scheduledAt: Timestamp.optional(),
  /** Anonymous joiners must pass Turnstile first. */
  challenge: z.boolean(),
  /** Asked with my bearer token and I'm a member of the host's workspace: I join as myself (no name, no lobby). */
  member: z.boolean().optional(),
  /**
   * Only people in the workspace can join (the link's "Who can join", or Settings → Huddles), and I'm not signed in as
   * one: `title` and `hostName` are empty, and joining needs signing in.
   */
  restricted: z.boolean().optional(),
});
export type PublicMeeting = z.infer<typeof PublicMeeting>;

/** Join from the link. With a member's bearer token the name and challenge are skipped. */
export const MeetingJoinRequest = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  turnstileToken: z.string().max(4096).optional(),
  muted: z.boolean().default(false),
  video: z.boolean().default(false),
});
export type MeetingJoinRequest = z.input<typeof MeetingJoinRequest>;

export const MeetingAccess = z.enum(["waiting", "admitted", "denied", "removed", "ended"]);
export type MeetingAccess = z.infer<typeof MeetingAccess>;

export const MeetingChatMessage = z.object({
  seq: z.number().int(),
  authorId: Id,
  authorName: z.string(),
  external: z.boolean(),
  text: z.string(),
  ts: Timestamp,
});
export type MeetingChatMessage = z.infer<typeof MeetingChatMessage>;

/** Join result / poll result for link clients (every ~2 s; it also counts as the heartbeat). */
export const MeetingStateResponse = z.object({
  status: MeetingAccess,
  /** Meeting token (Bearer for /v1/meet/:code/*): scoped to this huddle, short-lived; renewed in polls. */
  token: z.string(),
  tokenExpiresAt: Timestamp,
  /** Me in this huddle (a guest id, or my user id when I'm a member). */
  participantId: Id,
  external: z.boolean(),
  title: z.string(),
  /** While admitted. Guests never get `lobby`. */
  huddle: Huddle.optional(),
  media: HuddleMedia.optional(),
  /** Chat after the `after` cursor. */
  chat: z.array(MeetingChatMessage),
});
export type MeetingStateResponse = z.infer<typeof MeetingStateResponse>;

export const PostChatRequest = z.object({ text: z.string().trim().min(1).max(2000) });
export type PostChatRequest = z.infer<typeof PostChatRequest>;
export const ChatResponse = z.object({ messages: z.array(MeetingChatMessage) });
export type ChatResponse = z.infer<typeof ChatResponse>;

/**
 * Ring people into a huddle I'm in (at most 20 at a time): the conversation's members, or any member of the workspace
 * for a standalone link huddle. They get a notification.
 */
export const RingHuddleRequest = z.object({ userIds: z.array(Id).min(1).max(20) });
export type RingHuddleRequest = z.infer<typeof RingHuddleRequest>;

/**
 * Decline a ring ("Not now" on an incoming huddle): `callerId` (who rang me) hears about it — a realtime
 * `notification` with `about: "huddle"` and `declinedBy` (no push) while they're still in that huddle.
 */
export const DeclineHuddleRequest = z.object({ callerId: Id });
export type DeclineHuddleRequest = z.infer<typeof DeclineHuddleRequest>;

// ---------- Captions, transcript, AI notes, recordings, drawing (Slack huddle parity) ----------

/** One caption line: what a participant said, transcribed from their microphone. */
export const HuddleCaption = z.object({
  id: Id,
  huddleId: Id,
  channelId: Id,
  userId: Id,
  /** Display name at the time (guests: the name they typed). */
  name: z.string(),
  text: z.string(),
  ts: Timestamp,
  /** When the phrase began (server clock); `words` count from here. */
  startedAt: Timestamp.optional(),
  words: z.array(CaptionWord).max(400).optional(),
  source: CaptionSource.optional(),
});
export type HuddleCaption = z.infer<typeof HuddleCaption>;

/**
 * On-device transcription ("client" mode): one finished phrase of my own speech, as text and word timings only (no
 * audio). `ageMs`: how long ago the phrase began, so the server places it on its own clock (no clock skew).
 */
export const PostCaptionRequest = z.object({
  text: z.string().trim().min(1).max(1000),
  words: z.array(CaptionWord).max(400).optional(),
  ageMs: z.number().int().nonnegative().max(10 * 60_000).optional(),
});
export type PostCaptionRequest = z.infer<typeof PostCaptionRequest>;
/** `caption` is absent when the clip held no speech. */
export const CaptionResponse = z.object({ caption: HuddleCaption.optional() });
export type CaptionResponse = z.infer<typeof CaptionResponse>;

/**
 * A recording I made, uploaded (files.ts, for the huddle's conversation; purpose "huddle" for a link huddle): posted
 * to the huddle's thread, or kept on a link huddle's page.
 */
export const PostRecordingRequest = z.object({
  fileId: Id,
  durationMs: z.number().int().nonnegative().max(24 * 60 * 60 * 1000),
});
export type PostRecordingRequest = z.infer<typeof PostRecordingRequest>;

/** The reply in the huddle's thread; absent for a link huddle (its page keeps the recording). */
export const PostRecordingResponse = z.object({ message: Message.optional() });
export type PostRecordingResponse = z.infer<typeof PostRecordingResponse>;

/**
 * A stroke drawn on a shared screen (Slack's screen-share drawing). Points are fractions (0…1) of the shared picture,
 * so they line up at any size. Strokes fade a few seconds after the pen is lifted (core logic/huddle `strokeOpacity`).
 */
export const HuddleStroke = z.object({
  /** Client-made id; later frames with the same id add points. */
  id: z.string().min(1).max(80),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  /** New points of this frame, flattened: x0, y0, x1, y1, … (at most 64 points). */
  points: z.array(z.number().min(0).max(1)).max(128),
  /** The pen was lifted. */
  done: z.boolean().optional(),
});
export type HuddleStroke = z.infer<typeof HuddleStroke>;

/** A drawing change: more of a stroke, or strokes rubbed out with the eraser. */
export const HuddleDrawOp = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("stroke"), stroke: HuddleStroke }),
  z.object({ kind: z.literal("erase"), strokeIds: z.array(z.string().min(1).max(80)).min(1).max(50) }),
]);
export type HuddleDrawOp = z.infer<typeof HuddleDrawOp>;

/**
 * Live huddle signals over the realtime socket (ServerFrame `huddle`), never stored or replayed: caption lines and
 * drawing on the shared screen. Sent to the conversation's members; clients use the ones for the huddle they're in.
 */
export const HuddleSignal = z.discriminatedUnion("type", [
  z.object({ type: z.literal("huddle.caption"), caption: HuddleCaption }),
  z.object({ type: z.literal("huddle.draw"), huddleId: Id, channelId: Id, userId: Id, op: HuddleDrawOp }),
]);
export type HuddleSignal = z.infer<typeof HuddleSignal>;

// ---------- The Huddles area (works with Chat off) ----------
// Every huddle someone can reach without a conversation: link huddles started from the area, the conversation
// huddles of mine that are live, the links I host or was invited to, and the huddles I was in (Recent: kept, as
// Google Meet keeps its recordings, transcripts and notes until someone deletes them).

/** A huddle that ended, for Recent and its page. */
export const HuddleRecord = z.object({
  huddleId: Id,
  /** The conversation, or `meeting:<code>` for a link huddle without one. */
  channelId: Id,
  /** A link huddle's name (conversation huddles are named after the conversation). */
  title: z.string().optional(),
  code: MeetingCode.optional(),
  startedBy: Id,
  startedAt: Timestamp,
  endedAt: Timestamp,
  /** Members who were in the call, in join order. */
  participantIds: z.array(Id),
  /** People from outside the workspace who joined from the link (the names they typed). */
  guests: z.array(z.string()),
  /** The huddle message in the conversation (its thread has the recordings), when there is one. */
  messageId: Id.optional(),
  /** Someone had captions on: there is a transcript. */
  transcript: z.boolean(),
  /** People wrote in the call's chat. */
  chat: z.boolean(),
  /** AI notes were written. */
  notes: z.boolean(),
});
export type HuddleRecord = z.infer<typeof HuddleRecord>;

/** The Huddles area's data. Live conversation huddles also arrive in bootstrap and `huddle.*` events. */
export const HuddlesOverviewResponse = z.object({
  /** Live huddles I can join: conversation huddles of mine, and link huddles I host, am in or was invited to. */
  live: z.array(Huddle),
  /** Links I host or was invited to (Upcoming = the scheduled ones; My links = the ones I host). */
  links: z.array(Meeting),
  /** Huddles I was in, newest first. */
  recent: z.array(HuddleRecord),
});
export type HuddlesOverviewResponse = z.infer<typeof HuddlesOverviewResponse>;

/** A recording of the call, played on the huddle's page by the people who were in it. */
export const HuddleRecordingFile = z.object({
  file: FileRef,
  /** Who recorded it. */
  userId: Id,
  createdAt: Timestamp,
  /** Absent for a conversation huddle's recordings (replies in its thread). */
  durationMs: z.number().int().nonnegative().optional(),
});
export type HuddleRecordingFile = z.infer<typeof HuddleRecordingFile>;

/** A past huddle's page: the record, its transcript, the call's chat, the AI notes and the recordings. */
export const HuddleRecordResponse = z.object({
  record: HuddleRecord,
  transcript: z.array(HuddleTranscriptLine),
  chatMessages: z.array(MeetingChatMessage),
  notes: HuddleNotes.optional(),
  /** The AI notes are being written (the page loads again to show them). */
  notesPending: z.boolean().optional(),
  /** The oldest first; absent when there are none. */
  recordings: z.array(HuddleRecordingFile).optional(),
});
export type HuddleRecordResponse = z.infer<typeof HuddleRecordResponse>;
